const DEVICE_ID_KEY = "forge_device_id";
const TRUSTED_DEVICE_TOKEN_PREFIX = "forge_mfa_trusted_device_";
const TRUSTED_DEVICE_EXPIRY_PREFIX = "forge_mfa_trusted_expiry_";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value: string | null): value is string {
  if (!value) return false;
  return UUID_REGEX.test(value);
}

export function getDeviceId(): string | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(DEVICE_ID_KEY);
  if (isValidUuid(stored)) {
    return stored;
  }

  const generated = crypto.randomUUID();
  window.localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

export function setDeviceId(deviceId: string) {
  if (typeof window === "undefined") return;
  if (!isValidUuid(deviceId)) return;
  window.localStorage.setItem(DEVICE_ID_KEY, deviceId);
}

/**
 * Get trusted device token only if it hasn't expired.
 * Returns null if token is expired or doesn't exist.
 */
export function getTrustedDeviceToken(deviceId: string | null): string | null {
  if (typeof window === "undefined" || !deviceId) return null;

  const token = window.localStorage.getItem(`${TRUSTED_DEVICE_TOKEN_PREFIX}${deviceId}`);
  if (!token) return null;

  const expiryStr = window.localStorage.getItem(`${TRUSTED_DEVICE_EXPIRY_PREFIX}${deviceId}`);
  if (!expiryStr) {
    // Token exists but no expiry - clear it and return null
    clearTrustedDeviceToken(deviceId);
    return null;
  }

  const expiryTime = parseInt(expiryStr, 10);
  if (isNaN(expiryTime) || Date.now() >= expiryTime) {
    // Token expired - clear it and return null
    clearTrustedDeviceToken(deviceId);
    return null;
  }

  return token;
}

/**
 * Set trusted device token with a specific expiration timestamp.
 * @param deviceId - Device identifier
 * @param token - MFA trusted device token
 * @param expiresAtMs - Token expiration timestamp in milliseconds since epoch
 */
export function setTrustedDeviceToken(deviceId: string, token: string, expiresAtMs: number) {
  if (typeof window === "undefined") return;
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return;

  window.localStorage.setItem(`${TRUSTED_DEVICE_TOKEN_PREFIX}${deviceId}`, token);
  window.localStorage.setItem(`${TRUSTED_DEVICE_EXPIRY_PREFIX}${deviceId}`, expiresAtMs.toString());
}

export function clearTrustedDeviceToken(deviceId: string) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(`${TRUSTED_DEVICE_TOKEN_PREFIX}${deviceId}`);
  window.localStorage.removeItem(`${TRUSTED_DEVICE_EXPIRY_PREFIX}${deviceId}`);
}

// ---------------------------------------------------------------------------
// Legacy MFA satisfaction cleanup
//
// Historical client-side MFA satisfaction flags are removed on sign-out to
// avoid stale artifacts during the Supabase-only MFA flow.
// ---------------------------------------------------------------------------

const MFA_SATISFIED_PREFIX = "forge_mfa_satisfied_";
const MFA_SATISFIED_EXPIRY_PREFIX = "forge_mfa_satisfied_exp_";

/** Clear all MFA satisfaction flags (for sign-out when user id is unknown). */
export function clearAllMfaSatisfied() {
  if (typeof window === "undefined") return;
  const keysToRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(MFA_SATISFIED_PREFIX) || key?.startsWith(MFA_SATISFIED_EXPIRY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => window.localStorage.removeItem(k));
}
